import InlineEditor from "../../../components/inlineEditor/InlineEditor";
import { MAX_NUMBER_SM } from "../../../constants";
import SubformService from "../../../services/SubformService";
import { IconButton, TextButton } from "../../../components/button/Button";
import { useTranslation } from "../../../state/i18n";
import { createSignal, For, Show } from "solid-js";
import type { Subform } from "../../../state/formVersion";
import { CloseTrigger } from "../../../components/drawer/Drawer";
import Text from "../../../components/text/Text";
import SeparatorLine from "../../../components/SeparatorLine";
import HeaderValidationButton from "../header/HeaderValidationButton";
import type { SubformEditorTab } from "./types";
import HeaderLink from "../../../components/link/HeaderLink";
import { useFormServices } from "../../../services/formServices";

type SubformEditorHeaderProps = {
  subform: Subform;
  activeItem: SubformEditorTab;
  onMenuChange: (menu: SubformEditorTab) => void;
};

const SubformEditorHeader = (props: SubformEditorHeaderProps) => {
  const { t } = useTranslation();
  const [editingName, setEditingName] = createSignal(false);

  const mainLinks = [
    { label: t(($) => $.SUBFORMS_EDITOR_LINK_EDITOR), key: "fields" },
    { label: t(($) => $.SUBFORMS_EDITOR_LINK_RULES), key: "rules" },
    { label: t(($) => $.SUBFORMS_EDITOR_LINK_INTEGRATIONS), key: "mail" },
  ] satisfies { label: string; key: SubformEditorTab }[];

  const { validationService } = useFormServices();

  return (
    <header class="flex w-full items-center justify-between border-b border-subtle py-1.5 pr-4 pl-3">
      <div class="flex h-7.5 items-center gap-x-2">
        <div class="flex gap-x-2 pl-2">
          <For each={mainLinks}>
            {(link) => (
              <HeaderLink
                as="button"
                label={link.label}
                active={props.activeItem === link.key}
                onClick={() => props.onMenuChange(link.key)}
              />
            )}
          </For>
        </div>
      </div>
      <div class="min-w-0 text-medium-semibold text-primary">
        <InlineEditor
          maxLength={MAX_NUMBER_SM}
          value={props.subform.meta.name}
          setValue={(value) => {
            SubformService.updateSubformName(props.subform.uid, value);
          }}
          label={t(($) => $.SUBFORMS_EDIT_FORM_NAME)}
          open={editingName()}
          setOpen={setEditingName}
          displayView={() => (
            <div class="flex items-center gap-1">
              <Text as="h2" text={props.subform.meta.name} truncate />
              <IconButton
                icon="pencil"
                size="medium"
                variant="tertiary"
                aria-label={t(($) => $.EDIT_ARIA)}
                onClick={() => setEditingName(true)}
              />
            </div>
          )}
        />
      </div>
      <div class="flex h-9.5">
        {/* oxlint-disable-next-line better-tailwindcss/no-restricted-classes, better-tailwindcss/enforce-canonical-classes */}
        <div class="flex w-[3.9375rem]">
          <Show when={validationService.hasErrors()}>
            <HeaderValidationButton activeItem={props.activeItem} />
            <SeparatorLine orientation="vertical" class="mx-3 h-full" />
          </Show>
        </div>
        <CloseTrigger
          asChild={(parentProps) => <TextButton {...parentProps} text={t(($) => $.SUBFORMS_EDITOR_CLOSE)} />}
        />
      </div>
    </header>
  );
};

export default SubformEditorHeader;
